import { client, authHeaders } from '../../_shared/services/openapiClient';
import type { paths } from '../../../types/schema';

// Types derived from OpenAPI schema
export type SearchBettieQuery = paths['/api/v1/bettie/search']['get']['parameters']['query'];
export type SearchBettieResponse =
  paths['/api/v1/bettie/search']['get']['responses'][200]['content']['application/json'];

export type CreateChatRoomBody = NonNullable<
  paths['/api/v1/bettie/create_chat_room']['post']['requestBody']
>['content']['application/json'];
export type CreateChatRoomResponse =
  paths['/api/v1/bettie/create_chat_room']['post']['responses'][200]['content']['application/json'];

export type CreateMessageBody = NonNullable<
  paths['/api/v1/bettie/create_message']['post']['requestBody']
>['content']['application/json'];
export type CreateMessageResponse =
  paths['/api/v1/bettie/create_message']['post']['responses'][201]['content']['application/json'];

export type UpdateBettieProfileBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}']['put']['requestBody']
>['content']['application/json'];
export type UpdateBettieProfileResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}']['put']['responses'][200]['content']['application/json'];

export type GetBettieChatRoomsCountResponse =
  paths['/api/v1/bettie/chat_rooms_count']['get']['responses'][200]['content']['application/json'];

export type ResendBettieChatMessageResponse =
  paths['/api/v1/bettie/chat_messages/{chat_room_message_id}/resend']['post']['responses'][201]['content']['application/json'];

export type GetBettieDailyCheckInStatusResponse = NonNullable<
  paths['/api/v1/bettie/daily_check_in_reward_status']['get']['responses'][200]['content']['application/json']
>['bettieTransactions'];

export type ClaimBettieDailyCheckInRewardResponse =
  paths['/api/v1/bettie/daily_check_in_reward']['post']['responses'][200]['content']['application/json'];

export type UpdateBettieMoodSelectionBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['requestBody']
>['content']['application/json'];
export type UpdateBettieMoodSelectionResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['responses'][200]['content']['application/json'];

export async function searchBettie(params: SearchBettieQuery): Promise<SearchBettieResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/search', {
    params: { query: params },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response from bettie search');
  return data;
}

export async function createBettieChatRoom(
  body: CreateChatRoomBody
): Promise<CreateChatRoomResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/create_chat_room', {
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating chat room');
  return data;
}

export async function createBettieMessage(body: CreateMessageBody): Promise<CreateMessageResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/create_message', {
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating message');
  return data;
}

export async function updateBettieProfile(
  body: UpdateBettieProfileBody,
  chatRoomId: string
): Promise<UpdateBettieProfileResponse> {
  const { data, error } = await client.PUT('/api/v1/bettie/chat_rooms/{chat_room_id}', {
    params: { path: { chat_room_id: chatRoomId } },
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when updating bettie profile');
  return data;
}

export async function getBettieChatRoomsCount(): Promise<GetBettieChatRoomsCountResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/chat_rooms_count', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie chat rooms count');
  return data;
}

export async function resendBettieChatMessage(
  chatRoomMessageId: string
): Promise<ResendBettieChatMessageResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_messages/{chat_room_message_id}/resend',
    {
      params: { path: { chat_room_message_id: chatRoomMessageId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when resending bettie chat message');
  return data;
}

export async function deleteBettieChatMessage(chatRoomMessageId: string): Promise<void> {
  const { error } = await client.DELETE(
    '/api/v1/bettie/chat_messages/{chat_room_message_id}/delete',
    {
      params: { path: { chat_room_message_id: chatRoomMessageId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  return;
}

export async function getBettieDailyCheckInStatus(): Promise<GetBettieDailyCheckInStatusResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/daily_check_in_reward_status', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie daily check in reward status');
  return data.bettieTransactions;
}

export async function claimBettieDailyCheckInReward(): Promise<ClaimBettieDailyCheckInRewardResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/daily_check_in_reward', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when claiming bettie daily check in reward');
  return data;
}

export async function updateBettieMoodSelection(
  chatRoomId: string,
  body: UpdateBettieMoodSelectionBody
): Promise<UpdateBettieMoodSelectionResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/update_context',
    {
      params: { path: { chat_room_id: chatRoomId } },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when updating bettie mood selection');
  return data;
}